/* The upgrade prompt — STORY-033.
 *
 * Markup and class names are `design/components/upgrade-prompt`'s. Two of its
 * decisions are behaviour rather than styling:
 *
 * **It leads with what the paid plan gives.** The lead paragraph is the gain,
 * then the limit, then the free way round. A limit somebody runs into should
 * teach rather than scold, and the entire difference is which sentence comes
 * first — so the order is fixed by the component instead of left to each caller.
 *
 * **`informational` renders no call to action at all.** Apple's guideline
 * 3.1.3(f) permits a free companion to a paid web tool only while there is no
 * purchasing inside it *and no call to action for purchase outside it*, and
 * Google's consumption-only exception is equivalent. The stylesheet also hides
 * the actions for that variant, but this omits them: a control hidden only by
 * CSS comes back the moment a stylesheet fails to load, and the rule it would
 * break is a store rule rather than a cosmetic one.
 *
 * It still explains the refusal. Hiding the reason to satisfy a store rule
 * would make the app feel broken, which is the opposite of what this is for.
 */

import type { ReactNode } from 'react';

export type UpgradeTone =
  /** The web: may name a price and offer a way to pay. */
  | 'offer'
  /** The native apps: states the limit and the plan, and stops. */
  | 'informational';

export interface UpgradePromptProps {
  /** What the paid plan gives. Rendered first, always. */
  readonly lead: string;
  /** What is being used, of what. */
  readonly limit: string;
  /** The way round it without paying. */
  readonly alternative: ReactNode;
  /** The action's label. Ignored when `tone` is `informational`. */
  readonly action?: string;
  readonly onAction?: () => void;
  /** Names the whole prompt for assistive technology. */
  readonly label: string;
  readonly tone?: UpgradeTone;
}

export function UpgradePrompt({
  lead,
  limit,
  alternative,
  action,
  onAction,
  label,
  tone = 'offer',
}: UpgradePromptProps) {
  const informational = tone === 'informational';
  const classes = ['upgrade', informational ? 'upgrade--informational' : null]
    .filter(Boolean)
    .join(' ');

  return (
    /* A labelled region, so it is announced as one thing rather than as three
     * loose paragraphs somebody has to assemble. A <section> with an accessible
     * name IS that region — `design.md` takes semantic HTML over an ARIA role
     * saying the same thing, and Biome flags the role for the same reason. */
    <section className={classes} aria-label={label}>
      <p className="upgrade__lead">{lead}</p>
      <p className="upgrade__limit">{limit}</p>
      <p className="upgrade__alternative">{alternative}</p>

      {informational || !action ? null : (
        <div className="upgrade__actions">
          <button className="btn btn--primary" type="button" onClick={onAction}>
            {action}
          </button>
        </div>
      )}
    </section>
  );
}
